import {
  SceneLoader,
  Scene,
  FreeCamera,
  Vector3,
  StandardMaterial,
  Quaternion,
  ActionManager,
  InterpolateValueAction,
  AbstractMesh,
  Texture,
  PBRMaterial,
  VideoTexture,
  Material,
  Color3,
  ExecuteCodeAction,
} from 'babylonjs'
import * as GUI from 'babylonjs-gui'
import { VariantType } from '../../type/backendType'
import {
  IColor,
  ICompletedContextState,
  IModel,
  ITexture,
  IVariant,
} from '../../type/modelType'
import { VRProduct } from '../../type/vrType'
import { Context3D } from '../3d-context'
import {
  changeColorByMeshName,
  changeTextureByMeshName,
  switchingVariant,
} from '../design/customization'
import { setVrGUI } from '../design/vrCustomization'
import { getProductPackages } from '../modelEngine/product'
import { addDirectionalLight, attachShadow } from './light'

import Hls from 'hls.js'

export const setupScene = (context = Context3D.get()) => {
  if (!context.engine) return

  // Create a basic BJS Scene object
  const scene = new Scene(context.engine)
  Context3D.set({ scene })

  return scene
}

export function setupLight() {
  const light1 = addDirectionalLight({
    name: 'light_shadow_1',
    intensity: 0.2,
    position: [0, 100, 0],
  })
  if (light1) attachShadow(light1)
}

export const setCustomInput = (camera: FreeCamera) => {
  // @ts-ignore
  camera.inputs.attached.keyboard.keysUp.push(87)
  // @ts-ignore
  camera.inputs.attached.keyboard.keysDown.push(83)
  // @ts-ignore
  camera.inputs.attached.keyboard.keysLeft.push(65)
  // @ts-ignore
  camera.inputs.attached.keyboard.keysRight.push(68)
}

export const setupFpsCamera = (context = Context3D.get()) => {
  if (context.scene && context.canvas3D) {
    const camera = new FreeCamera(
      'fpsCam',
      new Vector3(-0.5, 1.7, -1),
      context.scene
    )
    camera.setTarget(new Vector3(-5, 1.1, -4))
    camera.applyGravity = true
    camera.ellipsoid = new Vector3(0.1, 0.9, 0.1)
    camera.ellipsoidOffset = new Vector3(0, 0, 0)
    camera.checkCollisions = true

    camera.attachControl(context.canvas3D, true)
    camera.speed = 0.1

    camera.minZ = 0.01

    setCustomInput(camera)

    Context3D.set({ camera: camera })
  }
}

const loadVideoTexture = (mesh: AbstractMesh, context = Context3D.get()) => {
  const scene = context.scene

  if (scene) {
    var stream1 =
      'https://etlive-mediapackage-fastly.cbsaavideo.com/dvr/manifest.m3u8'
    const video = document.createElement('video')
    video.setAttribute('src', stream1)
    video.setAttribute('preload', 'none')
    video.setAttribute('autoplay', 'false')
    video.setAttribute('playsinline', 'true')
    video.volume = 0.2
    document.documentElement.querySelector('body')?.appendChild(video)
    console.log('Adding HTML video element')

    if (video) {
      const videoMat = new StandardMaterial('textVid', scene)
      const videoTexture = new VideoTexture('video', video, scene, true, true)

      mesh.actionManager = new ActionManager(scene)

      videoMat.backFaceCulling = false
      videoMat.diffuseTexture = videoTexture
      videoMat.emissiveColor = Color3.White()

      mesh.material = videoMat
      var htmlVideo = videoTexture.video

      if (Hls.isSupported()) {
        var hls = new Hls()
        hls.loadSource(stream1)
        hls.attachMedia(video)
        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          mesh.actionManager?.registerAction(
            new ExecuteCodeAction(ActionManager.OnPickTrigger, function (
              event
            ) {
              htmlVideo.play()
            })
          )
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = stream1
        video.addEventListener('loadedmetadata', function () {
          mesh.actionManager?.registerAction(
            new ExecuteCodeAction(ActionManager.OnPickTrigger, function (
              event
            ) {
              htmlVideo.play()
            })
          )
        })
      }
    }
  }
}

export const loadRoom = async (context = Context3D.get()) => {
  const scene = context.scene

  if (scene) {
    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'showroom-compressed.glb',
      scene,
      function (meshes) {
        meshes.forEach(mesh => {
          mesh.receiveShadows = true
          mesh.isPickable = false
        })
      }
    )

    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'light-compressed.glb',
      scene,
      function (meshes) {
        meshes.forEach(mesh => {
          mesh.isPickable = false
        })
      }
    )

    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'tvscreen.glb',
      scene,
      function (meshes) {
        meshes.forEach(mesh => {
          mesh.isPickable = false

          if (mesh.name === 'TVScreen') {
            mesh.isPickable = true
            loadVideoTexture(mesh)
          }
        })
      }
    )

    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'furniture-compressed.glb',
      scene,
      function (meshes) {
        meshes.forEach(mesh => {
          // mesh.receiveShadows = true
          mesh.isPickable = false
        })
      }
    )

    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'tableset-compressed.glb',
      scene,
      function (meshes) {
        meshes.forEach(mesh => {
          // mesh.receiveShadows = true
          mesh.isPickable = false
        })
      }
    )
  }
}

export const loadPathway = async (context = Context3D.get()) => {
  const scene = context.scene

  if (scene) {
    SceneLoader.ImportMesh(
      '',
      '/assets/',
      'showroom-pathway.glb',
      scene,
      function (meshes) {
        const pathwayMaterial = new StandardMaterial('pathwayMaterial', scene)

        pathwayMaterial.alpha = 0
        // do something with the scene
        meshes.forEach(mesh => {
          if (mesh.name === 'PathWay') {
            mesh.material = pathwayMaterial
            mesh.checkCollisions = true
            mesh.isPickable = false
          }
        })
      }
    )
  }
}

const setProductByConfig = (mesh: AbstractMesh, model: IModel) => {
  const groupName = mesh.name.split('-')[0]
  const meshGroupConfig = model.meshes.find(m => m.meshGroup === groupName)

  const meshConfig = meshGroupConfig?.variants.find(
    v => v.meshName === mesh.name
  )

  const colorConfig = meshGroupConfig?.colors?.find(c => c.default)
  const textureConfig = meshGroupConfig?.textures?.find(t => t.default)

  if (meshGroupConfig && meshConfig?.default) {
    switchingVariant(mesh.name, meshGroupConfig.variants, model.id)
  }

  if (colorConfig) {
    changeColorByMeshName(colorConfig, [mesh.name], model.id)
  }

  if (textureConfig) {
    changeTextureByMeshName(textureConfig, [mesh.name], model.id)
  }
}

export const setProducts = async (
  products: VRProduct[],
  context: ICompletedContextState,
  scene = Context3D.get().scene
) => {
  if (scene) {
    const productPackages = getProductPackages(context)

    products.forEach(p => {
      const product = productPackages.find(
        productPackage => productPackage.id === p.productPackageId
      )

      if (product) {
        try {
          const model: IModel = JSON.parse(product.meta)

          if (model.source) {
            const splitPos = model.source.lastIndexOf('/')
            const base = model.source.substring(0, splitPos + 1)
            const fileName = model.source.substring(
              splitPos + 1,
              model.source.length
            )

            SceneLoader.ImportMesh(
              '',
              base,
              fileName,
              scene,
              function (meshes) {
                const root = meshes[0]
                root.name = model.id
                root.scaling = new Vector3(
                  p.transform.scale.x,
                  p.transform.scale.y,
                  p.transform.scale.z
                )
                root.position = new Vector3(
                  p.position.x,
                  p.position.y,
                  p.position.z
                )

                root.rotate(
                  new Vector3(1, 0, 0),
                  (p.transform.rotation.x * Math.PI) / 180
                ) // x axis
                root.rotate(
                  new Vector3(0, 1, 0),
                  (p.transform.rotation.y * Math.PI) / 180
                ) // y axis
                root.rotate(
                  new Vector3(0, 0, 1),
                  (p.transform.rotation.z * Math.PI) / 180
                ) // z axis

                //   root.actionManager = new ActionManager(scene);
                //   root.actionManager.registerAction(
                //     new InterpolateValueAction(
                //         ActionManager.OnPickTrigger,
                //         root,
                //         'visibility',
                //         0.2,
                //         1000
                //     )
                // );

                root.isPickable = true

                meshes.forEach(mesh => {
                  mesh.receiveShadows = true

                  if (mesh.material) {
                    mesh.material.name = `mat-${model.id}-${Math.random()}` //random new material name
                  }

                  setProductByConfig(mesh, model)
                })
              }
            )
          }
        } catch (e) {
          throw e
          // throw new Error('product meta is not correct.')
        }
      }
    })
  }
}

export const enableGUI = (model: IModel) => {
  removeGUI()

  const advancedTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI('UI')

  setVrGUI(advancedTexture, model)

  Context3D.set({ fullScreenUI: advancedTexture })
}

export const removeGUI = () => {
  const context3d = Context3D.get()
  if (context3d.fullScreenUI) {
    context3d.fullScreenUI.dispose()
    Context3D.set({ fullScreenUI: null })
  }
}

export const addDOMEvents = (
  products: VRProduct[],
  context: ICompletedContextState,
  context3d = Context3D.get()
) => {
  if (context3d.scene) {
    context3d.scene.onPointerUp = function (evt, pickResult) {
      if (pickResult?.hit && pickResult?.pickedMesh) {
        const productPackages = getProductPackages(context)

        const product = products.find(
          p =>
            p.modelId === pickResult.pickedMesh?.name ||
            p.modelId === pickResult.pickedMesh?.parent?.name
        )

        const productPackage = productPackages.find(
          productPackage => productPackage.id === product?.productPackageId
        )

        if (productPackage) {
          try {
            const model: IModel = JSON.parse(productPackage.meta)

            enableGUI(model)
          } catch (e) {
            throw e
            // throw new Error('product meta is not correct.')
          }
        }
      }
    }
  }
}
